export const imageKitUploadUrl =
  "https://upload.imagekit.io/api/v2/files/upload";

export type ImageKitUploadAuth = {
  token: string;
  fileName: string;
  folder: string;
  checks: string;
  useUniqueFileName: boolean;
  overwriteFile: boolean;
  renderingBaseUrl: string;
};

export type ImageKitImageInput = {
  provider: "imagekit";
  imagekitFileId: string;
  imagekitPath: string;
  renderingBaseUrl: string;
  width: number;
  height: number;
  format: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const imageFormatFromMimeType = (mimeType: string): string => {
  switch (mimeType.toLowerCase()) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/gif":
      return "gif";
    default:
      throw new Error("Only JPEG, PNG, and GIF images can be uploaded");
  }
};

export const imageFormatFromFileName = (fileName: string): string => {
  switch (fileName.split(".").pop()?.toLowerCase()) {
    case "jpeg":
    case "jpg":
      return "jpg";
    case "png":
      return "png";
    case "gif":
      return "gif";
    default:
      throw new Error("Only JPEG, PNG, and GIF images can be uploaded");
  }
};

export const normalizeImageKitUpload = (
  value: unknown,
  auth: ImageKitUploadAuth
): ImageKitImageInput => {
  if (
    !isRecord(value) ||
    typeof value.fileId !== "string" ||
    !value.fileId ||
    typeof value.filePath !== "string" ||
    value.filePath !== `${auth.folder}/${auth.fileName}` ||
    typeof value.width !== "number" ||
    value.width <= 0 ||
    typeof value.height !== "number" ||
    value.height <= 0
  ) {
    throw new Error("ImageKit returned invalid image metadata");
  }

  const extension = auth.fileName.split(".").pop()?.toLowerCase();
  if (!extension || !["jpg", "png", "gif"].includes(extension)) {
    throw new Error("ImageKit returned an unsupported image format");
  }

  return {
    provider: "imagekit",
    imagekitFileId: value.fileId,
    imagekitPath: value.filePath,
    renderingBaseUrl: auth.renderingBaseUrl,
    width: value.width,
    height: value.height,
    format: extension,
  };
};

export const uploadImageKitFile = async (
  file: Blob,
  auth: ImageKitUploadAuth,
  send: typeof fetch = fetch
): Promise<ImageKitImageInput> => {
  const body = new FormData();
  body.append("file", file);
  body.append("token", auth.token);
  body.append("fileName", auth.fileName);
  body.append("folder", auth.folder);
  body.append("useUniqueFileName", String(auth.useUniqueFileName));
  body.append("overwriteFile", String(auth.overwriteFile));
  body.append("checks", auth.checks);

  const response = await send(imageKitUploadUrl, { method: "POST", body });
  if (!response.ok) {
    throw new Error(
      `ImageKit image upload failed with status ${response.status}`
    );
  }

  const result: unknown = await response.json();
  return normalizeImageKitUpload(result, auth);
};
